import React, { useEffect, useMemo, useState } from "react";
import { collection, getFirestore, onSnapshot } from "firebase/firestore";
import { useEventId } from "../EventContext";
import { toMillis } from "../utils/firestoreTime";

const API = import.meta.env.VITE_API_BASE_URL;

/**
 * The board behind the finish line.
 *
 * A Post-Finish Gate mat re-reads the chip a few metres past the line, so this
 * screen can call the runner up while they are still walking towards the photo
 * rig. It lists the most recent crossings, newest first.
 *
 * Ordered by the GATE crossing, not by placing: the point is who just came
 * through, and a runner who finished tenth still walks past the camera in the
 * order they arrive. The placing is shown on the row, not used to sort it.
 */

/** How many rows a TV can carry at this size without shrinking the names. */
const ROWS = 8;

/** A crossing older than this is stale — the runner has long since walked off. */
const STALE_MS = 15 * 60 * 1000;

/** Freshly arrived: highlighted so the operator's eye lands on the new row. */
const FRESH_MS = 20 * 1000;

const trimMs = (value) =>
  typeof value === "string" ? value.trim().replace(/\.\d+$/, "") : value;

/**
 * The finish time to print beside the name.
 *
 * `finishDuration` is computed per request by the API and is NOT stored, so a
 * page reading Firestore directly — as this one does — will not find it. The
 * stored fields are `finishTime` and `gunTime`, and on a manually timed event
 * `finishTime` holds a Timestamp rather than a duration, which is why anything
 * that is not an HH:MM:SS string is rejected instead of being stringified.
 */
const finishText = (result = {}) => {
  const elapsed = [result.finishDuration, result.finishTime, result.gunTime].find(
    (value) => typeof value === "string" && /^\d{1,3}:\d{2}:\d{2}/.test(value.trim()),
  );
  return elapsed ? trimMs(elapsed) : null;
};

/** Every checkpoint on this result that belongs to a post-finish gate. */
const gateCrossingOf = (result, gateIds) => {
  const checkpoints = result?.checkpoints || {};
  let latest = null;
  for (const id of gateIds) {
    const ms = toMillis(checkpoints[id]?.time);
    if (Number.isFinite(ms) && (latest === null || ms > latest)) latest = ms;
  }
  return latest;
};

/** Elapsed milliseconds → HH:MM:SS, for a clock that may pass ten hours. */
const spanText = (ms) => {
  const total = Math.floor(ms / 1000);
  return [
    Math.floor(total / 3600),
    Math.floor((total % 3600) / 60),
    total % 60,
  ]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
};

export const PhotoGate = () => {
  // The context holds { realEventId, slug }: the URL carries the slug, while
  // Firestore is keyed by the real id. Passing the object straight to
  // collection() threw "n.indexOf is not a function" from the path builder.
  const { realEventId: eventId } = useEventId();
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]);
  // Re-renders on a timer so "fresh" highlighting decays without a new write.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    fetch(`${API}/api/timing/event_timing`)
      .then((response) => (response.ok ? response.json() : []))
      .then((events) => {
        if (cancelled) return;
        setEvent((events || []).find((item) => item.eventId === eventId) || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    return onSnapshot(
      collection(getFirestore(), "event_timing", eventId, "results"),
      (snapshot) => {
        setResults(
          snapshot.docs.map((document) => ({ id: document.id, ...document.data() })),
        );
      },
    );
  }, [eventId]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Which checkpoint ids are gates. The ingest sanitises the id before using it
  // as a field path (a locationId may contain characters Firestore rejects), so
  // match the stored key the same way rather than the raw configured id.
  const gateIds = useMemo(() => {
    const ids = new Set();
    for (const category of event?.categories || []) {
      for (const checkpoint of category.checkpoints || []) {
        if ((checkpoint.type || "").toLowerCase() !== "postfinish") continue;
        const raw = checkpoint.locationId || checkpoint.name || "";
        ids.add(String(raw).replace(/[.*~/[\]]/g, "_"));
      }
    }
    return [...ids];
  }, [event]);

  const categoryName = useMemo(() => {
    const byId = new Map(
      (event?.categories || []).map((category) => [category.id, category.name]),
    );
    return (id) => byId.get(id) || id || "";
  }, [event]);

  // Per-category clock, in the three states the results page already uses:
  // counting down to the gun, running, or finished at the cut-off.
  //
  // The states are copied deliberately. Before the gun the difference is
  // negative and each component carries that sign — a category due in 1h36m
  // renders as "-2:-36:-17" — which is why the magnitude is taken and the
  // direction reported separately.
  const clocks = useMemo(() => {
    const finished = new Map();
    for (const result of results) {
      if (!result.categoryId) continue;
      if (result.finishMatTime || result.finishTime) {
        finished.set(result.categoryId, (finished.get(result.categoryId) || 0) + 1);
      }
    }
    return (event?.categories || [])
      .map((category) => {
        const startMs = toMillis(category.startTime);
        const cutMs = toMillis(category.cutOffTime);
        if (!Number.isFinite(startMs)) return null;
        const closed = Number.isFinite(cutMs) && now > cutMs;
        const diff = now - startMs;
        return {
          id: category.id,
          name: category.name || category.id,
          // Held at the cut-off once closed: a clock still climbing an hour
          // after a category shut reads as a race still in progress.
          span: Math.abs(closed ? cutMs - startMs : diff),
          beforeGun: diff < 0,
          closed,
          finishers: finished.get(category.id) || 0,
        };
      })
      .filter(Boolean);
  }, [event, results, now]);

  const rows = useMemo(() => {
    if (gateIds.length === 0) return [];
    return results
      .map((result) => ({ result, at: gateCrossingOf(result, gateIds) }))
      .filter((row) => row.at !== null && now - row.at < STALE_MS)
      .sort((a, b) => b.at - a.at)
      .slice(0, ROWS);
  }, [results, gateIds, now]);

  // A misconfigured board and a quiet moment in the race look the same to the
  // operator, so the reason is written to the console where they can find it.
  // It must not go on screen: this board hangs in front of spectators, and
  // setup instructions there read as a fault in the event itself.
  //
  // Declared before the early return below: a hook that runs only on some
  // renders changes the hook order, which React treats as an error.
  useEffect(() => {
    if (event && gateIds.length === 0) {
      console.warn(
        "[gate] No checkpoint of type 'postfinish' in this event — the board " +
          "will stay empty. Add one in admin and point the second mat at it.",
      );
    }
  }, [event, gateIds.length]);

  if (!eventId) return null;

  return (
    // Same shell as VenueDisplay so the two boards read as one system on the
    // same venue screens: viewport type (vw) for a large display, paired with
    // fixed px up to `md` because 1.4vw is under 6px on a phone.
    <main className="min-h-screen md:h-screen md:overflow-hidden bg-[#07111f] text-white flex flex-col font-sans">
      <header className="px-4 py-3 md:h-[12vh] md:px-[3vw] md:py-0 flex items-center justify-between border-b border-white/10 bg-[#0b1728]">
        <div className="min-w-0">
          <div className="text-[10px] md:text-[1.15vw] leading-[1.15] text-teal-300 font-bold">
            VIONIXASIA TIMING SYSTEM
          </div>
          <h1 className="text-lg md:text-[2.4vw] leading-tight font-black uppercase truncate">
            {event?.eventName || "Finish"}
          </h1>
        </div>
        <div className="text-right shrink-0 pl-3">
          <div className="text-xl md:text-[2.6vw] leading-[1.15] font-black tabular-nums">
            {new Date(now).toLocaleTimeString("en-GB", { hour12: false })}
          </div>
        </div>
      </header>

      <div className="px-4 py-2.5 md:h-[10vh] md:px-[3vw] md:py-0 flex items-center justify-between gap-3 bg-teal-500 text-[#07111f]">
        <div className="text-xl md:text-[2.7vw] leading-[1.15] font-black uppercase truncate">
          Through the gate
        </div>
        {/* Category clocks ride in the band, beside the title. As cards above
            the table they ate the height the names need, and the names are the
            reason this board exists. */}
        <div className="flex items-center gap-3 md:gap-[1.6vw] shrink-0">
          {clocks.length === 0 ? (
            <span className="text-sm md:text-[2vw] leading-[1.15] font-black">STANDING BY</span>
          ) : (
            clocks.map((clock) => (
              <div key={clock.id} className="text-right">
                <div className="text-[9px] md:text-[0.85vw] font-bold uppercase opacity-70 leading-none">
                  {clock.name}
                </div>
                <div
                  className={`text-sm md:text-[1.9vw] font-black tabular-nums leading-tight ${
                    clock.closed ? "opacity-50" : ""
                  }`}
                >
                  {clock.beforeGun && !clock.closed ? "\u2212" : ""}
                  {spanText(clock.span)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <section className="flex-1 min-h-0 flex flex-col gap-4 p-3 md:gap-[1.4vw] md:p-[1.4vw]">
        <div className="min-h-0 flex-1 rounded-xl md:rounded-[1.2vw] border border-white/10 bg-[#0b1728] overflow-hidden flex flex-col">
          <div className="shrink-0 grid grid-cols-[3rem_1fr_5rem] md:grid-cols-[6vw_1fr_12vw] gap-2 md:gap-0 px-3 py-2 md:px-[1.3vw] md:py-[0.7vw] bg-white/5 text-[10px] md:text-[0.95vw] leading-[1.15] uppercase text-slate-400 font-bold">
            <span>Bib</span>
            <span>Athlete</span>
            <span className="text-right">Time</span>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-white/10">
            {rows.map(({ result, at }) => {
              const fresh = now - at < FRESH_MS;
              const time = finishText(result);
              return (
                <div
                  key={result.id || result.bib}
                  className={`shrink-0 grid grid-cols-[3rem_1fr_5rem] md:grid-cols-[6vw_1fr_12vw] gap-2 md:gap-0 items-center px-3 py-2.5 md:px-[1.3vw] md:py-0 md:h-[8vh] transition-colors duration-500 ${
                    fresh ? "bg-teal-400/[0.14]" : ""
                  }`}
                >
                  <span
                    className={`text-base md:text-[1.6vw] leading-[1.15] font-black tabular-nums ${
                      fresh ? "text-teal-300" : "text-slate-400"
                    }`}
                  >
                    {result.bib || "-"}
                  </span>

                  <span className="min-w-0">
                    <span className="block text-sm md:text-[2vw] font-extrabold uppercase truncate md:pr-[1vw] leading-tight">
                      {result.name || "-"}
                    </span>
                    <span className="block text-[10px] md:text-[0.95vw] leading-[1.15] text-slate-400 uppercase truncate">
                      {categoryName(result.categoryId)}
                    </span>
                  </span>

                  {time ? (
                    <span className="text-right text-base md:text-[1.7vw] leading-[1.15] font-black text-teal-300 tabular-nums">
                      {time}
                    </span>
                  ) : (
                    // A gate crossing can arrive before the finish is written —
                    // a late message, or a missed read at the line. Say so
                    // rather than a dash that reads as "no result".
                    <span className="text-right text-[10px] md:text-[0.9vw] leading-[1.15] text-slate-500 uppercase">
                      pending
                    </span>
                  )}
                </div>
              );
            })}

            {!rows.length && (
              <div className="py-12 md:py-0 md:h-[52vh] flex items-center justify-center text-sm md:text-[1.5vw] leading-[1.15] text-slate-500">
                WAITING FOR FIRST CROSSING
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default PhotoGate;
